import React from 'react';
import { FaShieldAlt, FaLock, FaUserShield, FaEye, FaExclamationTriangle } from 'react-icons/fa';
import { motion } from 'framer-motion';

const SecurityPrivacy = ({ contentData }) => {
  const securityFeatures = [
    {
      icon: <FaLock />,
      title: contentData?.sm_card1_title,
      description: contentData?.sm_card1_info,
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: <FaUserShield />,
      title: contentData?.sm_card2_title,
      description: contentData?.sm_card2_info,
      color: "from-green-500 to-green-600"
    },
    {
      icon: <FaShieldAlt />,
      title: contentData?.sm_card3_title,
      description: contentData?.sm_card3_info,
      color: "from-purple-500 to-blue-600"
    },
    {
      icon: <FaEye />,
      title: contentData?.sm_card4_title,
      description: contentData?.sm_card4_info,
      color: "from-orange-500 to-orange-600"
    }
  ];

  const guidelines = [
    contentData?.sm_is_text1,
    contentData?.sm_is_text2,
    contentData?.sm_is_text3,
    contentData?.sm_is_text4
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Your Security Matters
          </h2>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            We implement the highest security standards to protect your data and access
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Security Features */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold mb-8 flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <FaShieldAlt className="text-white" />
              </div>
              Secure Access 🔒
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {securityFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="bg-gray-800/50 backdrop-blur-sm p-6 rounded-xl border border-gray-700 hover:border-gray-600 transition-all"
                >
                  <div className={`w-12 h-12 bg-gradient-to-r ${feature.color} rounded-lg flex items-center justify-center mb-4`}>
                    <div className="text-white text-lg">
                      {feature.icon}
                    </div>
                  </div>
                  <h4 className="font-bold text-lg mb-2">{feature.title}</h4>
                  <p className="text-gray-300 text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Privacy & Guidelines */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <div className="space-y-8">
              {/* Privacy Section */}
              <div>
                <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                    <FaUserShield className="text-white" />
                  </div>
                  Privacy Protected 🛡️
                </h3>
                
                <div className="bg-gray-800/50 backdrop-blur-sm p-6 rounded-xl border border-gray-700">
                  <ul className="space-y-4 text-sm">
                    {[
                      contentData?.sm_pp_text1,
                      contentData?.sm_pp_text2,
                      contentData?.sm_pp_text3,
                      contentData?.sm_pp_text4
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Guidelines Section */}
              <div>
                <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 rounded-lg flex items-center justify-center">
                    <FaExclamationTriangle className="text-white" />
                  </div>
                  Important Security Guidelines ⚠️
                </h3>
                
                <div className="bg-gradient-to-r from-red-900/20 to-red-800/10 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <ul className="space-y-3 text-sm">
                    {guidelines.map((guideline, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-red-100">{guideline}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Encryption Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-12 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-pink-600/20 backdrop-blur-sm p-6 rounded-2xl text-center border border-gray-700"
        >
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center">
              <FaLock className="text-white text-sm" />
            </div>
            <span className="font-bold text-lg">256-bit SSL Encryption</span>
          </div>
          <p className="text-gray-300">
            {contentData?.sm_256_bit_info}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default SecurityPrivacy;